import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, ShoppingCartIcon, CreditCardIcon, PackageIcon, BarChartIcon, ShieldIcon, TruckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function ECommerceService() {
  const features = [{
    icon: ShoppingCartIcon,
    title: 'Unlimited Products',
    description: 'Add as many products as you need with no limits'
  }, {
    icon: CreditCardIcon,
    title: 'Secure Payments',
    description: 'PayPal, Stripe, and major card payments'
  }, {
    icon: PackageIcon,
    title: 'Inventory Management',
    description: 'Track stock levels and get low-stock alerts'
  }, {
    icon: TruckIcon,
    title: 'Shipping Options',
    description: 'Flexible shipping rates and delivery tracking'
  }, {
    icon: BarChartIcon,
    title: 'Sales Analytics',
    description: 'Detailed reports on sales and customer behavior'
  }, {
    icon: ShieldIcon,
    title: 'SSL Security',
    description: 'Secure checkout with SSL encryption'
  }];
  const benefits = ['Start selling online in weeks, not months', 'Accept payments from anywhere in the world', 'Manage everything from one dashboard', 'Mobile-optimized for shopping on the go', 'SEO-ready to attract organic traffic', 'Scalable as your business grows'];
  const faqs = [{
    question: 'What payment methods can I accept?',
    answer: 'We integrate with PayPal, Stripe, and all major credit/debit cards. You can also add additional payment gateways as needed.'
  }, {
    question: 'Can I manage my inventory?',
    answer: 'Yes, our e-commerce platform includes full inventory management with stock tracking, low-stock alerts, and bulk updates.'
  }, {
    question: 'Is it mobile-friendly?',
    answer: 'Absolutely. All our e-commerce stores are fully responsive and optimized for mobile shopping, which accounts for over 50% of online purchases.'
  }, {
    question: 'What about shipping and taxes?',
    answer: 'We set up flexible shipping options including flat rate, free shipping thresholds, and location-based rates. Tax calculations can be automated based on your location.'
  }];
  return <>
      <Helmet data-id="element-2012">
        <title data-id="element-2013">E-Commerce Development | Bright Pixxel Studios – UK</title>
        <meta name="description" content="Professional e-commerce websites for UK businesses. Secure payments, inventory management, and everything you need to sell online." data-id="element-2014" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2015">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2016">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2017">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center" data-id="element-2018">
              <ShoppingCartIcon size={40} className="text-[#0DC3DC]" data-id="element-2019" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-2020">
              E-Commerce Development
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-2021">
              Launch your online store with a powerful, secure e-commerce
              platform designed to maximize sales and provide an exceptional
              shopping experience.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2022">
              <Link to="/pricing" className="bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-3 rounded-lg font-medium transition-colors shadow-lg" data-id="element-2023">
                Get a Free Quote
              </Link>
              <Link to="/portfolio" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#0C1B26] px-8 py-3 rounded-lg font-medium transition-colors" data-id="element-2024">
                See Portfolio
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Features Grid */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2025">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2026">
          <div className="max-w-6xl mx-auto" data-id="element-2027">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-2028">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2029">
                Everything You Need to Sell Online
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2030">
                Powerful features to run your online store
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2031">
              {features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl hover:shadow-lg transition-shadow" data-id="element-2032">
                  <div className="w-12 h-12 bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2033">
                    <feature.icon size={24} className="text-[#0DC3DC]" data-id="element-2034" />
                  </div>
                  <h3 className="text-xl font-bold mb-2" data-id="element-2035">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-2036">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* Benefits Section with Image */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2037">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2038">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2039">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-2040">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2041">
                Why Choose Our E-Commerce Solution?
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2042">
                We build e-commerce stores that are easy to manage, secure, and
                designed to convert visitors into customers.
              </p>
              <div className="space-y-4" data-id="element-2043">
                {benefits.map((benefit, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className="flex items-center gap-3" data-id="element-2044">
                    <div className="w-6 h-6 rounded-full bg-[#0DC3DC]/20 flex items-center justify-center flex-shrink-0" data-id="element-2045">
                      <CheckIcon size={14} className="text-[#0DC3DC]" data-id="element-2046" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300" data-id="element-2047">
                      {benefit}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="rounded-xl overflow-hidden shadow-xl" data-id="element-2048">
              <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="E-commerce online shopping on mobile device - Bright Pixxel Studios professional e-commerce development" className="w-full h-auto" loading="lazy" decoding="async" data-id="element-2049" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-white dark:bg-[#1E1E1E]" data-id="element-2050">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2051">
          <div className="max-w-3xl mx-auto" data-id="element-2052">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-2053">
              Common Questions
            </motion.h2>
            <div className="space-y-6" data-id="element-2054">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-[#F9FAFB] dark:bg-[#212C34] p-6 rounded-xl" data-id="element-2055">
                  <h3 className="text-lg font-bold mb-3 flex items-start gap-3" data-id="element-2056">
                    <span className="text-[#0DC3DC] text-2xl font-bold flex-shrink-0" data-id="element-2057">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 pl-9" data-id="element-2058">
                    {faq.answer}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2059">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2060">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2061">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6 text-white" data-id="element-2062">
              Ready to Start Selling Online?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/90 mb-8" data-id="element-2063">
              Get in touch today for a free consultation and quote for your
              e-commerce project.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-2064">
              <a href="mailto:info@bright-pixxel.com?subject=New%20Enquiry%20-%20E-Commerce" className="inline-block bg-[#0DC3DC] hover:bg-[#0DC3DC]/90 text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg text-lg" data-id="element-2065">
                Get Your Free Quote
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}